import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import {
  CalendarFeed,
  ExternalBooking,
  ExternalBookingUpdate,
  IntegrationConflict,
  IntegrationConnection,
  IntegrationConnectionPayload,
  IntegrationConnectionUpdate,
  IntegrationSyncRun,
  IntegrationOverviewItem,
} from '../types/integrationTypes';

type IntegrationState = {
  connections: IntegrationConnection[];
  externalBookings: ExternalBooking[];
  selectedBooking: ExternalBooking | null;
  syncRuns: IntegrationSyncRun[];
  conflicts: IntegrationConflict[];
  feed: CalendarFeed | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  overview: IntegrationOverviewItem[];
  overviewLoaded: boolean;
  externalBookingsLoading: boolean;
  externalBookingsError: string | null;
};

const initialState: IntegrationState = {
  connections: [],
  externalBookings: [],
  selectedBooking: null,
  syncRuns: [],
  conflicts: [],
  feed: null,
  isLoading: false,
  isSaving: false,
  error: null,
  overview: [],
  overviewLoaded: false,
  externalBookingsLoading: false,
  externalBookingsError: null,
};

const IntegrationSlice = createSlice({
  name: 'integrations',
  initialState,
  reducers: {
    getIntegrationConnections(state) {
      state.isLoading = true;
      state.error = null;
    },
    getIntegrationOverview(state) {
      state.isLoading = true;
      state.error = null;
      state.overviewLoaded = false;
    },
    setIntegrationOverview(
      state,
      action: PayloadAction<IntegrationOverviewItem[]>,
    ) {
      state.overview = action.payload;
      state.isLoading = false;
      state.overviewLoaded = true;
    },
    setIntegrationConnections(
      state,
      action: PayloadAction<IntegrationConnection[]>,
    ) {
      state.connections = action.payload;
      state.isLoading = false;
    },
    createIntegrationConnection(
      state,
      _action: PayloadAction<IntegrationConnectionPayload>,
    ) {
      state.isSaving = true;
      state.error = null;
    },
    addIntegrationConnection(
      state,
      action: PayloadAction<IntegrationConnection>,
    ) {
      state.connections.push(action.payload);
      state.isSaving = false;
    },
    updateIntegrationConnection(
      state,
      _action: PayloadAction<{
        id: number;
        data: IntegrationConnectionUpdate;
      }>,
    ) {
      state.isSaving = true;
      state.error = null;
    },
    replaceIntegrationConnection(
      state,
      action: PayloadAction<IntegrationConnection>,
    ) {
      state.connections = state.connections.map((connection) =>
        connection.id === action.payload.id ? action.payload : connection,
      );
      state.isSaving = false;
    },
    removeIntegrationConnection(state, _action: PayloadAction<number>) {
      state.isSaving = true;
      state.error = null;
    },
    deleteIntegrationConnection(state, action: PayloadAction<number>) {
      state.connections = state.connections.filter(
        (connection) => connection.id !== action.payload,
      );
      state.isSaving = false;
    },
    getExternalBookings(state) {
      state.externalBookingsLoading = true;
      state.externalBookingsError = null;
    },
    setExternalBookings(state, action: PayloadAction<ExternalBooking[]>) {
      state.externalBookings = action.payload;
      state.externalBookingsLoading = false;
      state.externalBookingsError = null;
    },
    setExternalBookingsError(state, action: PayloadAction<string>) {
      state.externalBookingsLoading = false;
      state.externalBookingsError = action.payload;
    },
    getExternalBooking(
      state,
      _action: PayloadAction<number>,
    ) {
      state.isLoading = true;
      state.error = null;
    },
    setSelectedExternalBooking(
      state,
      action: PayloadAction<ExternalBooking | null>,
    ) {
      state.selectedBooking = action.payload;
      state.isLoading = false;
    },
    updateExternalBooking(
      state,
      _action: PayloadAction<{
        id: number;
        data: ExternalBookingUpdate;
      }>,
    ) {
      state.isSaving = true;
      state.error = null;
    },
    replaceExternalBooking(
      state,
      action: PayloadAction<ExternalBooking>,
    ) {
      state.externalBookings = state.externalBookings.map((booking) =>
        booking.id === action.payload.id ? action.payload : booking,
      );
      state.selectedBooking =
        state.selectedBooking?.id === action.payload.id
          ? action.payload
          : state.selectedBooking;
      state.isSaving = false;
    },
    markExternalBookingViewed(state, _action: PayloadAction<number>) {},
    replaceExternalBookingViewed(
      state,
      action: PayloadAction<ExternalBooking>,
    ) {
      state.externalBookings = state.externalBookings.map((booking) =>
        booking.id === action.payload.id ? action.payload : booking,
      );
      state.selectedBooking =
        state.selectedBooking?.id === action.payload.id
          ? action.payload
          : state.selectedBooking;
    },
    getIntegrationRuns(state, _action: PayloadAction<number>) {
      state.isLoading = true;
    },
    setIntegrationRuns(state, action: PayloadAction<IntegrationSyncRun[]>) {
      state.syncRuns = action.payload;
      state.isLoading = false;
    },
    getIntegrationConflicts(state) {
      state.isLoading = true;
    },
    setIntegrationConflicts(
      state,
      action: PayloadAction<IntegrationConflict[]>,
    ) {
      state.conflicts = action.payload;
      state.isLoading = false;
    },
    rotateCalendarFeed(state, _action: PayloadAction<number>) {
      state.isSaving = true;
      state.error = null;
    },
    copyCalendarFeedUrl(
      state,
      _action: PayloadAction<{
        rentalObjectId: number;
        onSuccess: (url: string) => void;
        onError: (message: string) => void;
      }>,
    ) {
      state.isSaving = true;
      state.error = null;
    },
    finishCopyCalendarFeedUrl(state) {
      state.isSaving = false;
    },
    setCalendarFeed(state, action: PayloadAction<CalendarFeed>) {
      state.feed = action.payload;
      state.isSaving = false;
    },
    revokeCalendarFeed(state, _action: PayloadAction<number>) {
      state.isSaving = true;
      state.error = null;
    },
    clearCalendarFeed(state) {
      state.feed = null;
      state.isSaving = false;
    },
    setIntegrationError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.isLoading = false;
      state.isSaving = false;
      state.overviewLoaded = true;
    },
    clearIntegrationState() {
      return initialState;
    },
  },
});

export const {
  getIntegrationConnections,
  getIntegrationOverview,
  setIntegrationOverview,
  setIntegrationConnections,
  createIntegrationConnection,
  addIntegrationConnection,
  updateIntegrationConnection,
  replaceIntegrationConnection,
  removeIntegrationConnection,
  deleteIntegrationConnection,
  getExternalBookings,
  setExternalBookings,
  setExternalBookingsError,
  getExternalBooking,
  setSelectedExternalBooking,
  updateExternalBooking,
  replaceExternalBooking,
  markExternalBookingViewed,
  replaceExternalBookingViewed,
  getIntegrationRuns,
  setIntegrationRuns,
  getIntegrationConflicts,
  setIntegrationConflicts,
  rotateCalendarFeed,
  copyCalendarFeedUrl,
  finishCopyCalendarFeedUrl,
  setCalendarFeed,
  revokeCalendarFeed,
  clearCalendarFeed,
  setIntegrationError,
  clearIntegrationState,
} = IntegrationSlice.actions;

export default IntegrationSlice.reducer;
export const integrationName = IntegrationSlice.name;

export const IntegrationSelectors = {
  getConnections: (state: RootState) => state.integrations.connections,
  getExternalBookings: (state: RootState) => state.integrations.externalBookings,
  getExternalBookingsLoading: (state: RootState) => state.integrations.externalBookingsLoading,
  getExternalBookingsError: (state: RootState) => state.integrations.externalBookingsError,
  getSelectedBooking: (state: RootState) => state.integrations.selectedBooking,
  getRuns: (state: RootState) => state.integrations.syncRuns,
  getConflicts: (state: RootState) => state.integrations.conflicts,
  getFeed: (state: RootState) => state.integrations.feed,
  getIsLoading: (state: RootState) => state.integrations.isLoading,
  getIsSaving: (state: RootState) => state.integrations.isSaving,
  getError: (state: RootState) => state.integrations.error,
  getOverview: (state: RootState) => state.integrations.overview,
  getOverviewLoaded: (state: RootState) => state.integrations.overviewLoaded,
};
